import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2, RotateCcw, Bot, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";

const QUICK_PROMPTS = [
  {
    label: "Turn my business idea into a freelance business",
    seed: "I have a business idea I've been sitting on — how do I turn it into a freelance business that earns in ₹?",
  },
  {
    label: "Turn my job struggle into freelance liberation",
    seed: "I'm exhausted in my current job. Layoffs are everywhere and my boss is impossible. How do I escape into freelancing?",
  },
  {
    label: "How does Freelancity work & pay me in ₹?",
    seed: "How does Freelancity actually work, and how do I get paid in ₹?",
  },
];

const INITIAL_MSG = {
  role: "assistant",
  content:
    "Hi, I'm Avni 👋 your Freelancity guide. Tell me your business idea, your job struggle, or anything you're curious about — and I'll help you turn it into freelance income in ₹.",
};

export default function AvniSection({ seed }) {
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: "Hi, I'm Avni 👋 your Freelancity guide. Tell me your business idea, your job struggle, or anything you're curious about — and I'll help you turn it into freelance income in ₹." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const sessionIdRef = useRef(null);
  const scrollRef = useRef(null);
  const lastSeedTsRef = useRef(null);
  const nav = useNavigate();

  // Has the user actually exchanged a real reply with Avni?
  const userMsgs = messages.filter((m) => m.role === "user").length;
  const assistantWithContent = messages.filter((m) => m.role === "assistant" && m.content.trim()).length;
  const canHandoff = userMsgs >= 1 && assistantWithContent >= 2 && !busy;

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `avni-${Math.random().toString(36).slice(2, 10)}`;
    }
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // When parent provides a new seed (from the Reality quiz), pre-fill the input box
  useEffect(() => {
    if (seed && seed.text && seed.ts !== lastSeedTsRef.current) {
      lastSeedTsRef.current = seed.ts;
      setInput(seed.text);
    }
  }, [seed]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const history = messages.slice(-6).map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);

    try {
      const res = await fetch(`${API}/avni/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionIdRef.current, history }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            }
          } catch (e) { void e; }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I lost the connection. Try again in a sec?" };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const handoff = async () => {
    if (!canHandoff || handoffBusy) return;
    setHandoffBusy(true);
    try {
      const history = messages
        .filter((m, i) => m.content.trim() && !(i === 0 && m.role === "assistant"))
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/avni/synthesize`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ history }),
      });
      if (!res.ok) throw new Error("synth failed");
      const data = await res.json();
      nav("/build", { state: { fromAvni: true, resumeText: data.resume_text || "" } });
    } catch {
      // Fallback: pass raw transcript
      const raw = messages
        .filter((m, i) => m.content.trim() && !(i === 0 && m.role === "assistant"))
        .map((m) => (m.role === "user" ? `Me: ${m.content}` : `Avni: ${m.content}`))
        .join("\n\n");
      nav("/build", { state: { fromAvni: true, resumeText: raw } });
    } finally {
      setHandoffBusy(false);
    }
  };

  return (
    <section id="avni" data-testid="avni-section" className="py-24 bg-slate-50/60 border-t border-slate-200">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">AI Assistant</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight">
              Meet <span className="text-blue-600">Avni</span> — your freelance liberation guide.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Ask anything about Freelancity, turn a business idea into a freelancing business, or transform your job
              struggle into freelance income — all in ₹.
            </p>

            <div className="mt-6 space-y-2" data-testid="avni-quick-prompts">
              {QUICK_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p.seed)}
                  disabled={busy}
                  data-testid={`avni-prompt-${i}`}
                  className="group w-full text-left flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-blue-400 hover:bg-blue-50/40 transition disabled:opacity-60"
                >
                  <span className="text-sm font-medium text-slate-800">{p.label}</span>
                  <Sparkles size={14} className="text-blue-500 opacity-60 group-hover:opacity-100" />
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-blue-800 leading-relaxed">
              <strong>Note:</strong> Avni is a guide, not a guarantee. She helps you frame the next step — Freelancity handles the rest.
            </div>
          </div>

          {/* Right chat panel */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_-50px_rgba(15,23,42,0.25)] overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50/60 to-white">
                <span className="relative grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white">
                  <Bot size={18} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-white" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-slate-900">Avni · AI Assistant</p>
                  <p className="text-xs text-slate-500">Online · powered by GPT-5.2</p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  data-testid="avni-reset"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-600 px-2.5 py-1.5 rounded-md hover:bg-slate-100"
                >
                  <RotateCcw size={12} /> New chat
                </button>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                data-testid="avni-messages"
                className="px-5 py-5 space-y-4 max-h-[460px] min-h-[340px] overflow-y-auto"
              >
                {messages.map((m, i) => (
                  <Message key={i} role={m.role} content={m.content} loading={busy && i === messages.length - 1 && !m.content} />
                ))}
              </div>

              {/* Input */}
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-slate-100 p-3 flex gap-2"
                data-testid="avni-form"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Tell Avni your job struggle or business idea…"
                  data-testid="avni-input"
                  disabled={busy}
                  className="flex-1 rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-slate-50"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  data-testid="avni-send"
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 text-white px-4 py-3 text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  Ask
                </button>
              </form>

              {/* Handoff CTA */}
              {canHandoff && (
                <div
                  data-testid="avni-handoff"
                  className="border-t border-blue-100 bg-gradient-to-r from-blue-50/80 via-sky-50/60 to-blue-50/80 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-blue-600 text-white shrink-0">
                    <Sparkles size={16} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-slate-900 text-sm leading-tight">
                      Ready to turn this chat into a real AI agent?
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Avni will package your skills into a sellable freelancer profile in seconds — no resume needed.
                    </p>
                  </div>
                  <button
                    onClick={handoff}
                    disabled={handoffBusy}
                    data-testid="avni-handoff-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-blue-700 hover:-translate-y-0.5 transition shadow-[0_12px_28px_-14px_rgba(37,99,235,0.7)] disabled:opacity-70 disabled:translate-y-0 whitespace-nowrap"
                  >
                    {handoffBusy ? (
                      <>
                        <Loader2 className="animate-spin" size={14} /> Packaging…
                      </>
                    ) : (
                      <>
                        Turn this into my agent <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Message({ role, content, loading }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`avni-msg-${role}`}>
      {!isUser && (
        <div className="mr-3 mt-0.5 grid place-items-center w-8 h-8 rounded-full bg-blue-600 text-white shrink-0">
          <Bot size={14} />
        </div>
      )}
      <div
        className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-blue-600 text-white rounded-tr-sm"
            : "bg-slate-100 text-slate-800 rounded-tl-sm"
        }`}
      >
        {content || (loading ? (
          <span className="inline-flex gap-1">
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400" />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400" />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400" />
          </span>
        ) : null)}
      </div>
    </div>
  );
}
